import { useApolloClient } from '@apollo/client';
import { getQueryParams, removeProps } from '@arcadiapower/warbler';
import { createContext, useEffect, useMemo, PropsWithChildren, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAsync } from '~/hooks';
import { AsyncReturnType } from '~/typings/utils';
import { removeKeys } from '~/utils/for-warbler';
import { queryParamsRedirectPath } from '~/utils/redirects';
import { store } from '~/utils/store';
import { trackPageView } from '~/utils/tracking';
import {
  handleAmeren,
  handleDocusign,
  handleGoogle,
  handlePromoCode,
  handleResumingUser,
  handleUtilityIdAndZipCode,
  handleUtmParams,
} from './handlers';
import { ParsedQueryParams, UnparsedQueryParams } from './types';

export const QueryParamsContext = createContext({} as QueryParamsContextType);

type Props = PropsWithChildren<{
  search?: string;
}>;

export type QueryParamsContextType = ParsedQueryParams & {
  setValidatedProjectId?: (projectId: number | undefined) => void;
};
/**
 *  React hook that asynchronously loads data dependent on the current QueryParams.
 *  once loading is false the contents of context are usable.
 */
export const useParamHandlers = (
  params: UnparsedQueryParams,
  searchString: string,
): {
  loading: boolean;
  context: ParsedQueryParams;
} => {
  const apolloClient = useApolloClient();

  const { loading, value: validatedParams = [] } = useAsync((): Promise<ValidatedParams> => {
    return Promise.all([
      handleGoogle({ apolloClient, params, searchString }),
      handlePromoCode({ apolloClient, params }),
      handleResumingUser({ apolloClient, params }),
      handleUtilityIdAndZipCode({ apolloClient, params }),
    ]);
  });

  const {
    accountId,
    customPlanId,
    enrollmentOption,
    email,
    fName,
    firstName,
    inviteCode,
    lName,
    lastName,
    preferredBillingExperience,
    preferredEnrollment,
    phone,
    reason,
    terms,
    utilityAccountId,
    businessName,
  } = params;

  const [googleUser, promoCode, resumingUser, zipAndUtility] = validatedParams;
  if (googleUser?.data) {
    /**
     * If we have a google user, we store it in the store.
     * This is needed when user reloads the page accidentally when in google login
     * page. This must be reset once the user is redirected away from google sign up.
     */
    store.set('GOOGLE_USER', googleUser);
  }
  const amerenUser = handleAmeren({ params });
  const dashboardReason = location.pathname === '/dashboard' ? reason : undefined;

  const redirectPath = validatedParams.length
    ? queryParamsRedirectPath({ amerenUser, dashboardReason, googleUser, resumingUser })
    : null;

  const values: QueryParamsContextType = {
    accountId,
    amerenUser,
    customPlanId,
    dashboardReason,
    docusignUser: handleDocusign({ params }),
    email,
    enrollmentOption,
    firstName: firstName || fName,
    googleUser: googleUser?.data,
    googleUserError: googleUser?.error,
    inviteCode,
    lastName: lastName || lName,
    phone: phone && String(phone),
    preferredBillingExperience,
    preferredEnrollment,
    promoCode,
    redirectPath,
    // @ts-expect-error there is some type mimatch between what is coming from graphql
    // and how we are using userDocusignEnvelopes. TODO fix this.
    resumingUser: resumingUser?.data,
    terms,
    unparsedQueryParams: removeKeys(params, ['loginToken', 'verificationCode', 'code', 'state']),
    utilityAccountId,
    utilityId: zipAndUtility?.utilityId,
    utmParams: handleUtmParams({ params }),
    zipCode: zipAndUtility?.zipCode,
    businessName,
  };

  const cleanValues = removeProps(values, [null, undefined]) as ParsedQueryParams;
  return {
    loading,
    context: cleanValues,
  };
};

/**
 * Temporary fix for SUB-459.
 * TODO: Find the real reason query params are prefixed with amp_ and fix it.
 */
export const sanitizeParamsForAMP = (params: Record<string, any>): Record<string, any> => {
  const sanitizedParams: UnparsedQueryParams = {};
  for (const [key, value] of Object.entries(params)) {
    if (key.startsWith('amp_')) {
      sanitizedParams[key.replace('amp_', '')] = value;
    } else {
      sanitizedParams[key] = value;
    }
  }
  return sanitizedParams;
};
export const QueryParamsProvider = ({ children, search }: Props) => {
  const searchString = search || location.search;
  const navigate = useNavigate();
  const [validatedProjectId, setValidatedProjectId] = useState<number | undefined>(undefined);
  const unsanitizedParams = useMemo(() => getQueryParams(searchString), []) as UnparsedQueryParams;
  const params = sanitizeParamsForAMP(unsanitizedParams);
  const { loading, context } = useParamHandlers(params, searchString);

  useEffect(() => {
    trackPageView();
    navigate(window.location.pathname, { replace: true }); // clear qps from url
  }, []);

  return (
    <QueryParamsContext.Provider
      value={{ ...context, projectId: validatedProjectId, setValidatedProjectId }}
    >
      {loading ? '' : children}
    </QueryParamsContext.Provider>
  );
};

export type AmerenUser = ReturnType<typeof handleAmeren>;
export type GoogleUser = AsyncReturnType<typeof handleGoogle>;
export type ResumingUser = AsyncReturnType<typeof handleResumingUser>;

export type ValidatedParams = [
  GoogleUser,
  AsyncReturnType<typeof handlePromoCode>,
  ResumingUser,
  AsyncReturnType<typeof handleUtilityIdAndZipCode>,
];
